(() => {
if (customElements.get('timephoria-bundle-builder')) return;

class TimephoriaBundleBuilder extends HTMLElement {
  constructor() {
    super();
    this.tabs = [];
    this.lookFilters = [];
    this.tiers = [];
    this.strings = {};
    this.swatches = {};
    this.activeTab = 'all';
    this.activeLook = 'all';
    this.selected = [];
    this.cardState = new Map();
    this.adding = false;
    this.bundleConfiguredTracked = false;
    this.byobAttribution = {};
    this.modalProduct = null;
    this.modalVariant = null;
    this.modalVariantGroup = 'all';
    this.modalImageIndex = 0;
    this.modalLightbox = null;
    this.handleModalKeydown = (event) => {
      if (this.modalLightbox?.pswp) {
        if (event.key === 'Escape') {
          this.modalLightbox.pswp.close();
        }
        return;
      }
      if (event.key === 'Escape') {
        this.closeProductModal();
        this.closeHelpModal();
      }
      if (event.key === 'ArrowLeft') this.moveModalImage(-1);
      if (event.key === 'ArrowRight') this.moveModalImage(1);
    };
  }

  connectedCallback() {
    this.modalOnly = this.dataset.modalOnly === 'true';
    this.tabs = this.readJson('[data-tabs-json]', []).filter((tab) => (
      tab
      && tab.id
      && tab.label
      && Array.isArray(tab.products)
      && tab.products.length
    ));
    if (!this.tabs.some((tab) => tab.id === this.activeTab)) {
      this.activeTab = this.tabs[0]?.id || '';
    }
    this.lookFilters = this.readJson('[data-look-filters-json]', []).filter((filter) => (
      filter
      && filter.id
      && filter.label
    ));
    if (!this.lookFilters.some((filter) => filter.id === this.activeLook)) {
      this.activeLook = this.lookFilters[0]?.id || 'all';
    }
    this.tierCopies = this.readJson('[data-tiers-json]', []);
    this.tiers = this.buildDiscountTiers();
    this.strings = this.readJson('[data-strings-json]', {});
    this.swatches = this.parseSwatches(this.dataset.swatchList || '');
    this.skuTags = this.readJson('[data-sku-tags-json]', []);

    if (this.modalOnly) {
      this.modalEl = this.createProductModal();
      this.appendChild(this.modalEl);
      this.bindProductModalEvents();
      return;
    }

    this.tabsEl = this.querySelector('.tp-byob__tabs');
    this.lookFilterGroupEl = this.querySelector('[data-look-filter-group]');
    this.lookFiltersEl = this.querySelector('.tp-byob__look-filters');
    this.productsEl = this.querySelector('.tp-byob__products');
    this.summaryEl = this.querySelector('.tp-byob__summary');
    this.progressEl = this.querySelector('[data-progress]');
    this.selectedWrapEl = this.querySelector('[data-selected-wrap]');
    this.selectedEl = this.querySelector('[data-selected]');
    this.emptySlotsEl = this.querySelector('[data-empty-slots]');
    this.limitMessageEl = this.querySelector('[data-limit-message]');
    this.subtotalEl = this.querySelector('[data-subtotal]');
    this.addBundleButton = this.querySelector('.tp-byob__add-bundle');
    this.toggleButton = this.querySelector('.tp-byob__summary-toggle');
    this.toggleText = this.querySelector('.tp-byob__summary-toggle-text');
    this.viewDetailButton = this.querySelector('[data-view-detail]');
    this.summaryCountEl = this.querySelector('[data-summary-count]');
    this.addToastEl = this.querySelector('[data-add-toast]');
    this.addToastDetailEl = this.querySelector('[data-add-toast-detail]');
    this.addToastViewButton = this.querySelector('[data-add-toast-view]');
    this.addToastTimer = null;
    this.scrollIndicator = this.querySelector('.tp-byob__scroll-indicator span');
    this.selectedPrevButton = this.querySelector('[data-selected-prev]');
    this.selectedNextButton = this.querySelector('[data-selected-next]');
    this.helpModalEl = this.querySelector('[data-help-modal]');
    this.helpOpenButton = this.querySelector('[data-help-open]');
    this.modalEl = this.createProductModal();
    this.appendChild(this.modalEl);
    this.byobAttribution = this.initByobAttribution();
    this.trackByobEvent('byob_view');

    this.restoreSelectedFromStorage();
    this.applyPrefillFromUrl();
    this.applyFiltersFromUrl();
    this.renderLookFilters();
    this.renderTabs();
    this.renderProducts();
    this.renderProgress();
    this.renderSummary();
    this.bindEvents();
    this.setupMobileSummary();
  }

  readJson(selector, fallback) {
    try {
      const el = this.querySelector(selector);
      return el ? JSON.parse(el.textContent) : fallback;
    } catch (error) {
      console.error('Bundle JSON parse error:', error);
      return fallback;
    }
  }

  createProductModal() {
    const modal = document.createElement('div');
    modal.className = 'tp-byob-modal';
    modal.hidden = true;
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-label', this.strings.productDetails || 'Product details');
    return modal;
  }

  openHelpModal() {
    if (!this.helpModalEl) return;
    this.helpModalEl.hidden = false;
    document.documentElement.classList.add('tp-byob-modal-open');
    document.addEventListener('keydown', this.handleModalKeydown);
    this.helpModalEl.querySelector('[data-help-close]')?.focus();
  }

  closeHelpModal() {
    if (!this.helpModalEl || this.helpModalEl.hidden) return;
    this.helpModalEl.hidden = true;
    document.documentElement.classList.remove('tp-byob-modal-open');
    document.removeEventListener('keydown', this.handleModalKeydown);
  }

  get storageKey() {
    return 'timephoria-byob-selected';
  }

  get attributionStorageKey() {
    return 'timephoria-byob-attribution';
  }

  initByobAttribution() {
    const params = new URLSearchParams(window.location.search);
    const stored = this.readStoredAttribution();
    const hasUtm = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].some((key) => params.has(key));
    const explicitSource = params.get('byob_source');
    let source = explicitSource || '';

    if (!source) {
      source = hasUtm ? 'campaign' : 'direct';
    }

    const attribution = {
      byob_source: source,
      byob_origin_path: params.get('byob_origin') || this.getSameSiteReferrerPath() || '',
      byob_session_id: `byob-${Date.now()}-${Math.random().toString(16).slice(2)}`,
      utm_source: params.get('utm_source') || stored.utm_source || '',
      utm_medium: params.get('utm_medium') || stored.utm_medium || '',
      utm_campaign: params.get('utm_campaign') || stored.utm_campaign || '',
      utm_content: params.get('utm_content') || stored.utm_content || '',
      utm_term: params.get('utm_term') || stored.utm_term || '',
    };

    this.saveStoredAttribution(attribution);
    return attribution;
  }

  readStoredAttribution() {
    try {
      return JSON.parse(window.sessionStorage.getItem(this.attributionStorageKey) || '{}') || {};
    } catch (error) {
      return {};
    }
  }

  saveStoredAttribution(attribution) {
    try {
      window.sessionStorage.setItem(this.attributionStorageKey, JSON.stringify(attribution));
    } catch (error) {
      console.error('Bundle attribution save error:', error);
    }
  }

  getSameSiteReferrerPath() {
    try {
      if (!document.referrer) return '';
      const referrer = new URL(document.referrer);
      if (referrer.origin !== window.location.origin) return '';
      return `${referrer.pathname}${referrer.search}`;
    } catch (error) {
      return '';
    }
  }

  trackByobEvent(eventName, eventParams = {}) {
    const payload = {
      event: eventName,
      byob_source: this.byobAttribution.byob_source || 'direct',
      byob_origin_path: this.byobAttribution.byob_origin_path || '',
      byob_session_id: this.byobAttribution.byob_session_id || '',
      utm_source: this.byobAttribution.utm_source || '',
      utm_medium: this.byobAttribution.utm_medium || '',
      utm_campaign: this.byobAttribution.utm_campaign || '',
      utm_content: this.byobAttribution.utm_content || '',
      utm_term: this.byobAttribution.utm_term || '',
      page_location: window.location.href,
      page_path: window.location.pathname,
      currency: this.analyticsCurrency,
      ...eventParams,
    };

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);

    if (typeof window.gtag === 'function') {
      const { event, ...gtagParams } = payload;
      window.gtag('event', event, gtagParams);
    }
  }

  get analyticsCurrency() {
    return window.Shopify?.currency?.active || window.ShopifyAnalytics?.meta?.currency || '';
  }

  centsToAnalyticsValue(cents) {
    return Number(((Number(cents) || 0) / 100).toFixed(2));
  }

  get bybBaseAnalyticsParams() {
    return {
      byb_page_type: 'byb',
      byb_section_id: this.dataset.sectionId || '',
    };
  }

  get selectedFinalTotal() {
    return Math.max(0, this.selectedSubtotal - this.bundlePricing.amount);
  }

  productVariantToAnalyticsItem(product, variant, quantity = 1) {
    return {
      item_id: String(variant.id),
      item_name: product.title,
      item_variant: variant.title,
      item_sku: variant.sku || '',
      item_category: this.activeTab,
      product_id: String(product.id),
      product_handle: product.handle || '',
      variant_id: String(variant.id),
      price: this.centsToAnalyticsValue(variant.price),
      quantity,
    };
  }

  selectedItemToAnalyticsItem(item, quantity = 1) {
    return {
      item_id: String(item.variantId),
      item_name: item.productTitle,
      item_variant: item.variantTitle,
      item_sku: item.sku || '',
      item_category: item.category || '',
      product_id: String(item.productId),
      product_handle: item.productHandle || '',
      variant_id: String(item.variantId),
      price: this.centsToAnalyticsValue(item.price),
      quantity,
    };
  }

  selectedItemsToAnalyticsItems() {
    const grouped = this.selected.reduce((map, item) => {
      const key = String(item.variantId);
      map[key] = map[key] || { item, quantity: 0 };
      map[key].quantity += 1;
      return map;
    }, {});

    return Object.values(grouped).map(({ item, quantity }) => this.selectedItemToAnalyticsItem(item, quantity));
  }

  groupedSelectedItems(items = this.selected) {
    const grouped = items.reduce((map, item) => {
      const key = String(item.variantId);
      map[key] = map[key] || { item, quantity: 0 };
      map[key].quantity += 1;
      return map;
    }, {});

    return Object.values(grouped).sort((first, second) => {
      const firstKey = `${first.item.productTitle || ''} ${first.item.variantTitle || ''} ${first.item.variantId || ''}`;
      const secondKey = `${second.item.productTitle || ''} ${second.item.variantTitle || ''} ${second.item.variantId || ''}`;
      return firstKey.localeCompare(secondKey);
    });
  }

  buildBybContents(items = this.selected) {
    return this.groupedSelectedItems(items).map(({ item, quantity }) => {
      const variantTitle = item.variantTitle && item.variantTitle !== 'Default Title' ? ` (${item.variantTitle})` : '';
      const sku = item.sku ? ` / ${item.sku}` : '';
      return `${quantity}x ${item.productTitle || item.productHandle || item.variantId}${variantTitle}${sku}`;
    }).join(' + ');
  }

  buildBybSkuContents(items = this.selected) {
    return this.groupedSelectedItems(items).map(({ item, quantity }) => {
      const itemKey = item.sku || String(item.variantId || '');
      return `${itemKey} x${quantity}`;
    }).join(' + ');
  }

  buildBybComboId(items = this.selected) {
    return this.groupedSelectedItems(items).map(({ item, quantity }) => {
      const itemKey = item.sku || String(item.variantId || '');
      return `${itemKey}x${quantity}`;
    }).join('|');
  }

  buildSelectedItem(product, variant) {
    return {
      uid: `${variant.id}-${Date.now()}-${Math.random().toString(16).slice(2)}`,
      productId: product.id,
      productHandle: product.handle || '',
      productTitle: product.title,
      variantId: variant.id,
      variantTitle: variant.title,
      sku: variant.sku || '',
      price: variant.price,
      image: variant.image || product.image,
      category: this.activeTab,
    };
  }

  restoreSelectedFromStorage() {
    try {
      const stored = JSON.parse(window.sessionStorage.getItem(this.storageKey) || '[]');
      if (!Array.isArray(stored) || !stored.length) return;

      this.selected = stored
        .map((variantId) => {
          const match = this.findProductByVariantId(Number(variantId));
          if (!match || !match.variant.available) return null;
          return this.buildSelectedItem(match.product, match.variant);
        })
        .filter(Boolean);
    } catch (error) {
      console.error('Bundle storage restore error:', error);
    }
  }

  saveSelectedToStorage() {
    try {
      window.sessionStorage.setItem(
        this.storageKey,
        JSON.stringify(this.selected.map((item) => item.variantId))
      );
    } catch (error) {
      console.error('Bundle storage save error:', error);
    }
  }

  clearSelectedStorage() {
    try {
      window.sessionStorage.removeItem(this.storageKey);
    } catch (error) {
      console.error('Bundle storage clear error:', error);
    }
  }

  applyPrefillFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const variantId = Number(params.get('byob_variant'));
    if (!variantId) return;

    const match = this.findProductByVariantId(variantId);
    if (!match || !match.variant.available) return;

    this.activeTab = match.tab.id;
    this.cardState.set(String(match.product.id), match.variant);
    this.addVariantToSelection(match.product, match.variant, 'prefill');

    params.delete('byob_variant');
    const nextQuery = params.toString();
    const nextUrl = `${window.location.pathname}${nextQuery ? `?${nextQuery}` : ''}${window.location.hash}`;
    window.history.replaceState({}, '', nextUrl);
  }

  handleizeFilterToken(value) {
    return String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/['’]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  decodeFilterToken(value) {
    const raw = String(value || '').trim();
    if (!raw) return '';
    try {
      return decodeURIComponent(raw.replace(/\+/g, ' ')).trim();
    } catch (error) {
      return raw;
    }
  }

  resolveLookFilter(token) {
    const handle = this.handleizeFilterToken(this.decodeFilterToken(token));
    if (!handle) return null;
    return this.lookFilters.find((filter) => (
      this.handleizeFilterToken(filter.id) === handle
      || this.handleizeFilterToken(filter.label) === handle
      || this.handleizeFilterToken(filter.tag) === handle
    ))?.id || null;
  }

  resolveTabFilter(token) {
    const handle = this.handleizeFilterToken(this.decodeFilterToken(token));
    if (!handle) return null;
    return this.tabs.find((tab) => (
      this.handleizeFilterToken(tab.id) === handle
      || this.handleizeFilterToken(tab.label) === handle
    ))?.id || null;
  }

  parseFiltersFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const look = this.resolveLookFilter(params.get('look'));
    const tab = this.resolveTabFilter(params.get('category'));
    return {
      look,
      tab,
      hasLook: Boolean(look),
      hasTab: Boolean(tab),
    };
  }

  applyFiltersFromUrl() {
    const parsed = this.parseFiltersFromUrl();
    const defaultLook = this.lookFilters.some((filter) => filter.id === 'all')
      ? 'all'
      : (this.lookFilters[0]?.id || 'all');
    const defaultTab = this.tabs.some((tab) => tab.id === 'all')
      ? 'all'
      : (this.tabs[0]?.id || '');

    if (parsed.hasLook || parsed.hasTab) {
      this.activeLook = parsed.hasLook ? parsed.look : defaultLook;
      this.activeTab = parsed.hasTab ? parsed.tab : defaultTab;
    }

    this.syncFiltersToUrl();
  }

  syncFiltersToUrl() {
    if (window.Shopify?.designMode) return;

    const params = new URLSearchParams(window.location.search);
    if (this.activeLook && this.activeLook !== 'all') {
      params.set('look', this.activeLook);
    } else {
      params.delete('look');
    }
    if (this.activeTab && this.activeTab !== 'all') {
      params.set('category', this.activeTab);
    } else {
      params.delete('category');
    }

    const nextQuery = params.toString();
    const nextUrl = `${window.location.pathname}${nextQuery ? `?${nextQuery}` : ''}${window.location.hash}`;
    const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (currentUrl === nextUrl) return;
    window.history.replaceState({}, '', nextUrl);
  }

  parseSwatches(raw) {
    return raw.split('\n').reduce((map, line) => {
      const parts = line.split(':');
      if (parts.length >= 2) {
        const name = parts.shift().trim().toLowerCase();
        const value = this.getSwatchCssValue(parts.join(':'));
        if (name && value) map[name] = value;
      }
      return map;
    }, {});
  }

  getSwatchCssValue(rawValue) {
    const value = String(rawValue || '').trim();
    if (!value || /gradient\s*\(/i.test(value) || !value.includes(',')) return value;

    const colors = value.split(',').map((item) => {
      const color = item.trim();
      return /^[0-9a-f]{3}([0-9a-f]{3})?$/i.test(color) ? `#${color}` : color;
    }).filter(Boolean);
    if (colors.length < 2) return colors[0] || '';

    const band = 100 / colors.length;
    if (colors.length > 2) {
      const stops = colors.map((color, index) => (
        `${color} ${this.trimCssNumber(index * band)}% ${this.trimCssNumber((index + 1) * band)}%`
      ));
      return `conic-gradient(from ${this.trimCssNumber(-180 / colors.length)}deg, ${stops.join(', ')})`;
    }

    return `linear-gradient(to right, ${colors[0]} 0% 50%, ${colors[1]} 50% 100%)`;
  }

  trimCssNumber(value) {
    return Number(value.toFixed(4)).toString();
  }

  normalizeVariantValue(value) {
    return String(value || '').trim().toLowerCase();
  }

  getProductOptions(product) {
    return Array.isArray(product?.options) ? product.options : [];
  }

  getOptionValueName(value) {
    return String(value?.name ?? value ?? '').trim();
  }

  getVariantOptions(variant) {
    if (Array.isArray(variant?.options) && variant.options.length) return variant.options;
    return [variant?.option1, variant?.option2, variant?.option3].filter((value) => value != null);
  }

  getVariantOptionValue(variant, position) {
    if (!variant || !position) return '';
    return this.getOptionValueName(this.getVariantOptions(variant)[Number(position) - 1]);
  }

  isPcsValue(value) {
    const normalized = this.normalizeVariantValue(value).replace(/[\s-]+/g, '');
    return /\d+(?:pc|pcs)$/.test(normalized);
  }

  getPcsOption(product) {
    const options = this.getProductOptions(product);
    const explicitPosition = Number(product?.pcsOptionPosition || 0);
    const explicitOption = options.find((option) => Number(option?.position) === explicitPosition);
    if (explicitPosition && explicitOption) return explicitOption;

    return options.find((option) => (
      (option?.values || []).filter((value) => this.isPcsValue(this.getOptionValueName(value))).length > 1
    )) || null;
  }

  getShadeOption(product) {
    const options = this.getProductOptions(product);
    const explicitPosition = Number(product?.shadeOptionPosition || 0);
    const explicitOption = options.find((option) => Number(option?.position) === explicitPosition);
    if (explicitOption) return explicitOption;

    const namedOption = options.find((option) => /color|colour|shade/i.test(String(option?.name || '')));
    if (namedOption) return namedOption;

    const pcsOption = this.getPcsOption(product);
    return options.find((option) => Number(option?.position) !== Number(pcsOption?.position)) || options[0] || null;
  }

  getPcsValue(product, variant) {
    return this.getVariantOptionValue(variant, this.getPcsOption(product)?.position);
  }

  getVariantLabel(product, variant) {
    const shadeOption = this.getShadeOption(product);
    return this.getVariantOptionValue(variant, shadeOption?.position) || variant?.option1 || variant?.title || '';
  }

  getVariantsForPcs(product, pcsValueOrVariant = null) {
    const variants = Array.isArray(product?.variants) ? product.variants : [];
    const pcsOption = this.getPcsOption(product);
    if (!pcsOption) return variants;

    const pcsValue = typeof pcsValueOrVariant === 'string'
      ? pcsValueOrVariant
      : this.getPcsValue(product, pcsValueOrVariant);
    const normalizedPcsValue = this.normalizeVariantValue(pcsValue);
    if (!normalizedPcsValue) return variants;

    return variants.filter((variant) => (
      this.normalizeVariantValue(this.getVariantOptionValue(variant, pcsOption.position)) === normalizedPcsValue
    ));
  }

  getTargetVariantForPcs(product, pcsValue, selectedVariant) {
    const variants = this.getVariantsForPcs(product, pcsValue);
    if (!variants.length) return null;

    const pcsPosition = Number(this.getPcsOption(product)?.position || 0);
    const currentOptions = this.getVariantOptions(selectedVariant);
    const ranked = variants.map((variant, index) => ({
      variant,
      index,
      score: this.getVariantOptions(variant).reduce((score, value, optionIndex) => {
        if (optionIndex + 1 === pcsPosition) return score;
        return score + (
          this.normalizeVariantValue(value) === this.normalizeVariantValue(currentOptions[optionIndex]) ? 1 : 0
        );
      }, 0),
    })).sort((left, right) => (
      Number(right.variant.available) - Number(left.variant.available)
      || right.score - left.score
      || left.index - right.index
    ));

    return ranked[0]?.variant || null;
  }

  getUniqueShadeVariants(product, variants, selectedVariant = null) {
    const shadeOption = this.getShadeOption(product);
    if (!shadeOption) return [];

    const byLabel = new Map();
    (variants || []).forEach((variant) => {
      const label = this.getVariantLabel(product, variant);
      const key = this.normalizeVariantValue(label);
      if (!key || key === 'default title') return;
      const current = byLabel.get(key);
      if (
        !current
        || variant.id === selectedVariant?.id
        || (!current.available && variant.available)
      ) {
        byLabel.set(key, variant);
      }
    });
    return this.sortVariantsByColorOrder(product, [...byLabel.values()]);
  }

  sortVariantsByColorOrder(product, variants) {
    const order = Array.isArray(product?.colorSortOrder) ? product.colorSortOrder : [];
    if (!order.length || !Array.isArray(variants) || !variants.length) return variants || [];

    const rank = new Map();
    order.forEach((name, index) => {
      const key = this.normalizeVariantValue(name);
      if (key && !rank.has(key)) rank.set(key, index);
    });
    if (!rank.size) return variants;

    return [...variants].sort((left, right) => {
      const leftKey = this.normalizeVariantValue(this.getVariantLabel(product, left));
      const rightKey = this.normalizeVariantValue(this.getVariantLabel(product, right));
      const leftRank = rank.has(leftKey) ? rank.get(leftKey) : Number.MAX_SAFE_INTEGER;
      const rightRank = rank.has(rightKey) ? rank.get(rightKey) : Number.MAX_SAFE_INTEGER;
      return leftRank - rightRank;
    });
  }

  getPreferredVariant(product, variants = null, group = null) {
    const pool = Array.isArray(variants) && variants.length
      ? variants
      : (Array.isArray(product?.variants) ? product.variants : []);
    if (!pool.length) return null;

    const findInPool = (shadeName, { requireAvailable = false } = {}) => {
      const key = this.normalizeVariantValue(shadeName);
      if (!key) return null;
      const matches = pool.filter((variant) => (
        this.normalizeVariantValue(this.getVariantLabel(product, variant)) === key
      ));
      if (!matches.length) return null;
      const available = matches.find((variant) => variant.available);
      if (requireAvailable) return available || null;
      return available || matches[0];
    };

    const pickFirstFromOrder = (shadeOrder) => {
      if (!Array.isArray(shadeOrder) || !shadeOrder.length) return null;
      for (const shadeName of shadeOrder) {
        const availableMatch = findInPool(shadeName, { requireAvailable: true });
        if (availableMatch) return availableMatch;
      }
      return null;
    };

    // 命中全局色号排序：按排序顺延到第一个可售色号
    const colorSortOrder = Array.isArray(product?.colorSortOrder) ? product.colorSortOrder : [];
    if (colorSortOrder.length) {
      const sortedAvailable = pickFirstFromOrder(colorSortOrder);
      if (sortedAvailable) return sortedAvailable;
    }

    // 未命中排序：按当前分类配置原文顺序顺延到第一个可售
    if (group && group.id !== 'all' && Array.isArray(group.values) && group.values.length) {
      const groupAvailable = pickFirstFromOrder(group.values);
      if (groupAvailable) return groupAvailable;
    }

    return pool.find((variant) => variant.available) || pool[0] || null;
  }

  getVariantGroupKey(group, index) {
    if (!group || group.id) return group?.id || 'all';
    return `${index}-${String(group.label || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
  }

  normalizeVariantGroupLabel(label) {
    return String(label || '').trim().replace(/\s+/g, ' ');
  }

  getProductVariantGroups(product, variants = null) {
    const visibleShadeValues = Array.isArray(variants)
      ? new Set(variants.map((variant) => this.normalizeVariantValue(this.getVariantLabel(product, variant))).filter(Boolean))
      : null;
    const groups = Array.isArray(product?.variantGroups)
      ? product.variantGroups.reduce((items, group) => {
        const label = this.normalizeVariantGroupLabel(group?.label);
        const values = Array.isArray(group?.values) ? group.values : [];
        const hasVisibleValue = !visibleShadeValues || values.some((value) => (
          visibleShadeValues.has(this.normalizeVariantValue(value))
        ));
        if (
          label
          && values.length
          && hasVisibleValue
        ) {
          items.push({
            ...group,
            label,
          });
        }
        return items;
      }, [])
      : [];

    if (!groups.length) return [];

    return [
      {
        id: 'all',
        label: 'All',
        values: [],
      },
      ...groups.map((group, index) => ({
        id: this.getVariantGroupKey(group, index),
        label: group.label,
        values: group.values,
      })),
    ];
  }

  getModalVisibleVariants(product) {
    const variants = this.getVariantsForPcs(product, this.modalVariant);
    const groups = this.getProductVariantGroups(product, variants);
    const activeGroup = groups.find((group) => group.id === this.modalVariantGroup);

    if (!activeGroup || activeGroup.id === 'all') return variants;

    const values = new Set((activeGroup.values || []).map((value) => this.normalizeVariantValue(value)));
    return variants.filter((variant) => values.has(this.normalizeVariantValue(this.getVariantLabel(product, variant))));
  }

  renderModalVariantGroups(product) {
    const pcsVariants = this.getVariantsForPcs(product, this.modalVariant);
    const groups = this.getProductVariantGroups(product, pcsVariants);
    if (!groups.length) return '';

    if (!groups.some((group) => group.id === this.modalVariantGroup)) {
      this.modalVariantGroup = 'all';
    }

    return `
      <div class="tp-byob-modal__variant-groups" role="tablist" aria-label="${this.escape(product.title)} color categories">
        ${groups.map((group) => `
          <button
            class="tp-byob-modal__variant-group ${group.id === this.modalVariantGroup ? 'is-active' : ''}"
            type="button"
            role="tab"
            aria-selected="${group.id === this.modalVariantGroup ? 'true' : 'false'}"
            data-modal-variant-group="${this.escape(group.id)}"
          >
            ${this.escape(group.label)}
          </button>
        `).join('')}
      </div>
    `;
  }

  bindEvents() {
    this.lookFiltersEl?.addEventListener('click', (event) => {
      const button = event.target.closest('[data-look-filter]');
      if (!button) return;
      this.activeLook = button.dataset.lookFilter;
      this.syncFiltersToUrl();
      this.renderLookFilters();
      this.renderProducts();
    });

    this.tabsEl.addEventListener('click', (event) => {
      const button = event.target.closest('[data-tab]');
      if (!button) return;
      this.activeTab = button.dataset.tab;
      this.syncFiltersToUrl();
      this.renderTabs();
      this.renderProducts();
    });

    this.productsEl.addEventListener('click', (event) => {
      const modalTrigger = event.target.closest('[data-open-product]');
      if (modalTrigger) {
        event.preventDefault();
        this.openProductModal(modalTrigger.closest('[data-product-id]'));
        return;
      }

      const swatch = event.target.closest('[data-swatch-variant]');
      if (swatch) {
        this.selectCardVariant(swatch.closest('[data-product-id]'), Number(swatch.dataset.swatchVariant));
        return;
      }

      const addButton = event.target.closest('[data-add-product]');
      if (addButton) {
        this.addProduct(addButton.closest('[data-product-id]'), addButton);
      }
    });

    this.productsEl.addEventListener('mousedown', (event) => {
      if (event.target.closest('.tp-byob-card__title')) {
        event.stopPropagation();
      }
    });

    this.selectedEl.addEventListener('click', (event) => {
      const removeButton = event.target.closest('[data-remove-selected]');
      if (!removeButton) return;
      const removedItem = this.selected.find((item) => item.uid === removeButton.dataset.removeSelected);
      this.selected = this.selected.filter((item) => item.uid !== removeButton.dataset.removeSelected);
      if (this.selected.length < this.minItems) {
        this.bundleConfiguredTracked = false;
      }
      if (removedItem) {
        this.trackByobEvent('byob_remove_item', {
          item_count: this.selected.length,
          bundle_value: this.centsToAnalyticsValue(this.selectedSubtotal),
          items: [this.selectedItemToAnalyticsItem(removedItem)],
        });
      }
      this.renderSummary();
    });

    this.selectedEl.addEventListener('scroll', () => this.updateScrollIndicator(), { passive: true });

    this.selectedPrevButton?.addEventListener('click', () => this.scrollSelectedItems(-1));
    this.selectedNextButton?.addEventListener('click', () => this.scrollSelectedItems(1));

    this.toggleButton.addEventListener('click', () => {
      const expanded = !this.summaryEl.classList.contains('is-expanded');
      this.setSummaryExpanded(expanded);
      this.updateScrollIndicator();
    });

    this.viewDetailButton?.addEventListener('click', () => {
      this.setVariantDetailOpen(!this.summaryEl.classList.contains('is-detail-open'));
    });

    this.addToastViewButton?.addEventListener('click', () => {
      this.hideMobileAddToast();
      this.setSummaryExpanded(true);
      this.updateScrollIndicator();
      this.mobileSummaryUpdate?.();
      this.toggleButton?.focus();
    });

    this.addBundleButton.addEventListener('click', () => this.addBundleToCart());

    this.bindProductModalEvents();

    this.helpOpenButton?.addEventListener('click', () => this.openHelpModal());

    this.helpModalEl?.addEventListener('click', (event) => {
      const closeTrigger = event.target.closest('[data-help-close]');
      if (closeTrigger || event.target === this.helpModalEl) {
        this.closeHelpModal();
      }
    });
  }

  bindProductModalEvents() {
    this.modalEl.addEventListener('click', (event) => {
      const closeTrigger = event.target.closest('[data-modal-close]');
      if (closeTrigger || event.target === this.modalEl) {
        this.closeProductModal();
        return;
      }

      const pcsButton = event.target.closest('[data-modal-pcs-value]');
      if (pcsButton) {
        this.selectModalPcs(pcsButton.dataset.modalPcsValue);
        return;
      }

      const variantGroup = event.target.closest('[data-modal-variant-group]');
      if (variantGroup) {
        this.selectModalVariantGroup(variantGroup.dataset.modalVariantGroup);
        return;
      }

      const swatch = event.target.closest('[data-modal-swatch-variant]');
      if (swatch) {
        this.selectModalVariant(Number(swatch.dataset.modalSwatchVariant));
        return;
      }

      const galleryButton = event.target.closest('[data-modal-gallery]');
      if (galleryButton) {
        this.moveModalImage(Number(galleryButton.dataset.modalGallery));
        return;
      }

      const imageDot = event.target.closest('[data-modal-image-index]');
      if (imageDot) {
        this.selectModalImage(Number(imageDot.dataset.modalImageIndex));
        return;
      }

      const previewImage = event.target.closest('[data-modal-slide]');
      if (previewImage) {
        this.openModalImagePreview(Number(previewImage.dataset.modalSlide));
        return;
      }

      const addButton = event.target.closest('[data-modal-add-product]');
      if (addButton) {
        this.addModalProduct(addButton);
      }
    });

    this.modalEl.addEventListener('scroll', (event) => {
      if (event.target.matches('[data-modal-image-track]')) {
        this.updateModalImageFromScroll(event.target);
      }
    }, true);
  }

  setupMobileSummary() {
    const update = () => {
      if (!window.matchMedia('(max-width: 767px)').matches) {
        this.summaryEl.classList.remove('is-static-mobile');
        this.style.removeProperty('--mobile-summary-height');
        return;
      }

      const rect = this.getBoundingClientRect();
      const summaryHeight = this.summaryEl.offsetHeight || 0;
      const shouldStop = rect.bottom <= window.innerHeight + summaryHeight * 0.35;
      this.summaryEl.classList.toggle('is-static-mobile', shouldStop);
      this.style.setProperty('--mobile-summary-height', shouldStop ? '0px' : `${summaryHeight}px`);
    };

    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    this.mobileSummaryUpdate = update;
  }

  setSummaryExpanded(expanded) {
    if (!this.summaryEl || !this.toggleButton || !this.toggleText) return;

    this.summaryEl.classList.toggle('is-expanded', expanded);
    this.toggleButton.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    this.toggleText.textContent = expanded ? '' : this.strings.expand;
  }

  setVariantDetailOpen(open) {
    if (!this.summaryEl || !this.viewDetailButton) return;

    this.summaryEl.classList.toggle('is-detail-open', open);
    this.viewDetailButton.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  isMobileViewport() {
    return window.matchMedia('(max-width: 767px)').matches;
  }

  formatSelectedCountLabel(count) {
    const unit = count === 1 ? (this.strings.item || 'item') : (this.strings.items || 'items');
    return `· ${count} ${unit}`;
  }

  getSelectedItemDetail(item) {
    if (!item) return '';
    const variantTitle = item.variantTitle && item.variantTitle !== 'Default Title' ? item.variantTitle : '';
    return variantTitle ? `${item.productTitle} · ${variantTitle}` : (item.productTitle || '');
  }

  pulseElement(el, className) {
    if (!el) return;
    el.classList.remove(className);
    void el.offsetWidth;
    el.classList.add(className);
  }

  hideMobileAddToast() {
    if (this.addToastTimer) {
      window.clearTimeout(this.addToastTimer);
      this.addToastTimer = null;
    }
    this.addToastEl?.classList.remove('is-visible');
  }

  showMobileAddFeedback(item) {
    if (!this.isMobileViewport() || !item) return;

    if (this.addToastDetailEl) {
      this.addToastDetailEl.textContent = this.getSelectedItemDetail(item);
    }
    this.addToastEl?.classList.add('is-visible');
    this.pulseElement(this.summaryCountEl, 'is-pop');
    this.pulseElement(this.summaryEl, 'is-glow');

    if (this.addToastTimer) window.clearTimeout(this.addToastTimer);
    this.addToastTimer = window.setTimeout(() => {
      this.hideMobileAddToast();
    }, 2600);
  }

  get activeProducts() {
    const products = this.tabs.find((tab) => tab.id === this.activeTab)?.products || [];
    const activeFilter = this.lookFilters.find((filter) => filter.id === this.activeLook);
    const activeTag = activeFilter?.tag || '';
    if (!activeTag) return products;

    const normalizedActiveTag = this.normalizeTag(activeTag);
    return products.filter((product) => (
      product.tags || []
    ).some((tag) => this.normalizeTag(tag) === normalizedActiveTag));
  }

  get minItems() {
    return Number(this.dataset.minItems || 2);
  }

  get maxItems() {
    if (this.dataset.enableMax !== 'true') return null;
    return Number(this.dataset.maxItems || 30);
  }

  get quantityDiscountsEnabled() {
    const value = String(this.dataset.enableQuantityDiscounts ?? 'true').toLowerCase();
    return value !== 'false' && value !== '0' && value !== 'off';
  }

  get groupCartItems() {
    const value = String(this.dataset.groupCartItems ?? 'false').toLowerCase();
    return value === 'true' || value === '1' || value === 'on';
  }

  get discountRuleType() {
    const value = this.dataset.discountRuleType || 'item_percent';
    return ['item_percent', 'item_amount', 'spend_percent', 'spend_amount'].includes(value)
      ? value
      : 'item_percent';
  }

  isItemThreshold(tier) {
    return tier?.thresholdType === 'quantity' || tier?.thresholdType === 'item_amount';
  }

  isAmountReward(tier) {
    return tier?.thresholdType === 'item_amount' || tier?.thresholdType === 'spend_amount';
  }

  buildDiscountTiers() {
    const type = this.discountRuleType;
    const isItem = type === 'item_percent' || type === 'item_amount';
    const isPercent = type === 'item_percent' || type === 'spend_percent';
    const copies = Array.isArray(this.tierCopies) ? this.tierCopies : [];
    const levels = [1, 2, 3, 4].map((index) => ({
      target: Number(this.getAttribute(`data-quantity-discount-${index}-count`) || 0),
      reward: Number(this.getAttribute(`data-quantity-discount-${index}-percent`) || 0),
      copy: copies[index - 1] || {},
    }));

    return levels
      .filter((level) => level.target > 0 && level.reward > 0)
      .sort((a, b) => a.target - b.target)
      .map((level) => {
        const reward = isPercent ? Math.min(100, level.reward) : level.reward;
        return {
          thresholdType: isItem ? (isPercent ? 'quantity' : 'item_amount') : (isPercent ? 'spend_percent' : 'spend_amount'),
          count: isItem ? level.target : 0,
          spendUsdCents: isItem ? 0 : Math.round(level.target * 100),
          discount: isPercent ? reward : 0,
          discountAmountUsdCents: isPercent ? 0 : Math.round(reward * 100),
          label: level.copy.label || '',
          beforeText: level.copy.beforeText || '',
          reachedText: level.copy.reachedText || '',
        };
      });
  }

  get selectedSubtotal() {
    return this.selected.reduce((sum, item) => sum + Number(item.price || 0), 0);
  }

  tierTarget(tier) {
    if (this.isItemThreshold(tier)) return Number(tier.count) || 0;
    return this.convertBaseCentsToCurrentCents(tier.spendUsdCents);
  }

  tierProgressValue(tier) {
    return this.isItemThreshold(tier) ? this.selected.length : this.selectedSubtotal;
  }

  tierReached(tier) {
    const target = this.tierTarget(tier);
    return target > 0 && this.tierProgressValue(tier) >= target;
  }

  tierSavings(tier) {
    const total = this.selectedSubtotal;
    if (!tier || !this.tierReached(tier)) return 0;
    if (this.isAmountReward(tier)) {
      return Math.min(total, this.convertBaseCentsToCurrentCents(tier.discountAmountUsdCents));
    }
    return Math.round(total * Number(tier.discount || 0) / 100);
  }

  get reachedTiers() {
    return this.tiers.filter((tier) => this.tierReached(tier));
  }

  get currentTier() {
    return this.reachedTiers.reduce((best, tier) => (
      !best || this.tierSavings(tier) >= this.tierSavings(best) ? tier : best
    ), null);
  }

  get nextTier() {
    return this.tiers.find((tier) => !this.tierReached(tier)) || null;
  }

  getSummarySlotCount() {
    const tierMax = this.tiers.reduce((max, tier) => (
      this.isItemThreshold(tier) ? Math.max(max, Number(tier.count) || 0) : max
    ), 0);
    return Math.max(tierMax || 3, this.selected.length);
  }

  getSlotOfferLabel(slotNumber) {
    const tier = this.tiers.find((item) => this.isItemThreshold(item) && Number(item.count) === Number(slotNumber));
    if (!tier) return '';
    if (this.isAmountReward(tier)) {
      const amount = this.convertBaseCentsToCurrentCents(tier.discountAmountUsdCents);
      return amount > 0 ? `${this.formatMoney(amount)} OFF` : '';
    }

    const discount = Number(tier.discount || 0);
    return discount > 0 ? `${discount}% OFF` : '';
  }

  get bundlePricing() {
    const total = this.selectedSubtotal;
    if (!this.quantityDiscountsEnabled) {
      return { type: 'percent', percent: 0, amount: 0, badge: '' };
    }

    const tier = this.currentTier;
    const amount = tier ? this.tierSavings(tier) : 0;
    if (this.isAmountReward(tier)) {
      return {
        type: 'amount',
        percent: 0,
        amount,
        badge: amount > 0 ? `-${this.formatGoalMoney(amount)}` : '',
      };
    }

    const percent = Number(tier?.discount || 0);
    return {
      type: 'percent',
      percent,
      amount,
      badge: percent > 0 ? `-${percent}%` : '',
    };
  }

  get discountPercent() {
    return this.bundlePricing.percent;
  }

  get currencyRate() {
    const rate = Number(window.Shopify?.currency?.rate || 1);
    return Number.isFinite(rate) && rate > 0 ? rate : 1;
  }

  get currencyCode() {
    return window.Shopify?.currency?.active || window.Shopify?.currency?.currency || '';
  }

  convertBaseCentsToCurrentCents(cents) {
    return Math.round(Number(cents || 0) * this.currencyRate);
  }

  formatMoney(cents) {
    if (window.FoxTheme?.Currency?.formatMoney) {
      return window.FoxTheme.Currency.formatMoney(cents, this.dataset.moneyFormat || window.FoxTheme.settings?.moneyFormat);
    }
    if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents);
    return `$${(Number(cents || 0) / 100).toFixed(2)}`;
  }

  formatGoalMoney(cents) {
    const formatted = this.formatMoney(cents);
    const code = this.currencyCode;
    if (!code || formatted.includes(code)) return formatted;
    return `${formatted} ${code}`;
  }

  formatProgressMoney(cents) {
    return this.formatMoney(cents);
  }

  normalizeTag(value) {
    return String(value || '').trim().toLowerCase();
  }

  normalizeHexColor(value) {
    const color = String(value || '').trim();
    return /^#[0-9a-f]{3}([0-9a-f]{3})?$/i.test(color) ? color : '#202024';
  }

  renderTabs() {
    this.tabsEl.innerHTML = this.tabs.map((tab) => {
      const color = this.normalizeHexColor(tab.color);
      return `
        <button
          class="tp-byob__tab ${tab.id === this.activeTab ? 'is-active' : ''}"
          type="button"
          data-tab="${this.escape(tab.id)}"
          style="--tp-byob-filter-color:${this.escape(color)}"
        >
          ${this.escape(tab.label)}
        </button>
      `;
    }).join('');
  }

  renderLookFilters() {
    if (!this.lookFilterGroupEl || !this.lookFiltersEl) return;

    const filters = this.lookFilters.filter((filter) => filter.id && filter.label);
    this.lookFilterGroupEl.hidden = filters.length < 2;
    this.lookFiltersEl.innerHTML = filters.map((filter) => {
      const color = this.normalizeHexColor(filter.color);
      return `
        <button
          class="tp-byob__look-filter ${filter.id === this.activeLook ? 'is-active' : ''}"
          type="button"
          data-look-filter="${this.escape(filter.id)}"
          style="--tp-byob-filter-color:${this.escape(color)}"
        >
          ${this.escape(filter.label)}
        </button>
      `;
    }).join('');
  }

  renderProducts() {
    const products = this.activeProducts;
    this.productsEl.innerHTML = products.length
      ? products.map((product) => this.renderProductCard(product)).join('')
      : `<p class="tp-byob__empty-products">${this.escape(this.strings.noProducts || 'No products found.')}</p>`;
    this.refreshReviewBadges();
  }

  refreshReviewBadges() {
    const scope = this.productsEl;
    if (!scope || !window.jdgm || typeof window.jdgm.initializeWidgets !== 'function') return;

    try {
      window.jdgm.initializeWidgets(scope);
    } catch (error) {
      console.error(error);
    }
  }

  renderProductCard(product) {
    const availableVariants = (product.variants || []).filter((variant) => variant.available);
    const selectedVariant = this.cardState.get(String(product.id))
      || this.getPreferredVariant(product)
      || availableVariants[0]
      || product.variants?.[0];
    if (selectedVariant) this.cardState.set(String(product.id), selectedVariant);
    const image = selectedVariant?.image || product.image || '';
    const isAvailable = Boolean(selectedVariant?.available);
    const buttonLabel = isAvailable ? (this.strings.add || this.strings.addFallback) : this.strings.soldOut;
    const productUrl = this.getProductUrl(product, selectedVariant);
    const shortDescription = String(product.shortDescription || '').trim();
    const price = Number(selectedVariant?.price || 0);
    const compareAtPrice = Number(selectedVariant?.compareAtPrice || 0);
    const hasCompareAtPrice = compareAtPrice > price;

    return `
      <article class="tp-byob-card" role="listitem" data-product-id="${product.id}">
        <a class="tp-byob-card__image-link" href="${this.escape(productUrl)}" data-open-product aria-label="${this.escape(product.title)}">
          <div class="tp-byob-card__image-wrap">
            ${this.renderProductBadges(product, selectedVariant)}
            ${image ? `<img class="tp-byob-card__image" src="${this.escape(image)}" alt="${this.escape(product.title)}" loading="lazy" width="600" height="600">` : ''}
          </div>
        </a>
        <div class="tp-byob-card__content">
          <a class="tp-byob-card__title" href="${this.escape(productUrl)}" data-open-product aria-label="${this.escape(product.title)}">${this.escape(product.title)}</a>
          <p class="tp-byob-card__description ${shortDescription ? '' : 'is-empty'}">${shortDescription ? this.escape(shortDescription) : '&nbsp;'}</p>
          ${this.renderCardReviews(product)}
          <p class="tp-byob-card__price">
            <span class="tp-byob-card__price-current">${this.formatGoalMoney(price)}</span>
            ${hasCompareAtPrice ? `<span class="tp-byob-card__price-compare">${this.formatGoalMoney(compareAtPrice)}</span>` : ''}
          </p>
          ${this.renderCardSwatches(product, selectedVariant)}
        </div>
        <button class="tp-byob-card__button" type="button" data-add-product ${isAvailable ? '' : 'disabled'}>
          ${this.escape(buttonLabel)}
        </button>
      </article>
    `;
  }

  getPcsOptionValues(product) {
    const pcsOption = this.getPcsOption(product);
    if (!pcsOption) return [];

    const configuredValues = Array.isArray(pcsOption.values)
      ? pcsOption.values.map((value) => this.getOptionValueName(value)).filter(Boolean)
      : [];
    const values = configuredValues.length
      ? configuredValues
      : (product.variants || []).map((variant) => this.getVariantOptionValue(variant, pcsOption.position)).filter(Boolean);
    return [...new Map(values.map((value) => [this.normalizeVariantValue(value), value])).values()];
  }

  renderModalPcsOptions(product, selectedVariant) {
    const pcsOption = this.getPcsOption(product);
    const values = this.getPcsOptionValues(product);
    if (!pcsOption || values.length < 2) return '';

    const selectedValue = this.getPcsValue(product, selectedVariant);
    return `
      <div class="tp-byob-modal__pcs-options" aria-label="${this.escape(pcsOption.name || 'Quantity')}">
        ${values.map((value) => {
          const isActive = this.normalizeVariantValue(value) === this.normalizeVariantValue(selectedValue);
          const isAvailable = this.getVariantsForPcs(product, value).some((variant) => variant.available);
          return `
            <button
              class="tp-byob-modal__pcs-option ${isActive ? 'is-active' : ''}"
              type="button"
              data-modal-pcs-value="${this.escape(value)}"
              aria-pressed="${isActive ? 'true' : 'false'}"
              ${isAvailable ? '' : 'disabled'}
            >${this.escape(value)}</button>
          `;
        }).join('')}
      </div>
    `;
  }

  renderCardReviews(product) {
    const badge = String(product.reviewBadgeHtml || '').trim();
    return `
      <div class="tp-byob-card__reviews${badge ? '' : ' is-empty'}">
        <div class="jdgm-widget jdgm-preview-badge" data-id="${this.escape(product.id)}" data-template="collection">
          ${badge}
        </div>
      </div>
    `;
  }

  renderProductBadges(product, variant) {
    const newTagPrefix = 'tag__new_';
    const newTag = (product.tags || []).find((tag) => (
      String(tag).toLowerCase().startsWith(newTagPrefix)
    ));
    const newTagLabel = newTag ? String(newTag).slice(newTagPrefix.length).trim() : '';
    const newLabel = /^new$/i.test(newTagLabel) ? 'New' : newTagLabel;
    const price = Number(variant?.price || 0);
    const compareAtPrice = Number(variant?.compareAtPrice || 0);
    const discountPercentage = compareAtPrice > price
      ? Math.round(((compareAtPrice - price) * 100) / compareAtPrice)
      : 0;

    if (!newLabel && !discountPercentage) return '';

    return `
      <div class="tp-byob-card__badges" aria-hidden="true">
        ${newLabel ? `<span class="tp-byob-card__badge tp-byob-card__badge--new f-badge f-badge--new">${this.escape(newLabel)}</span>` : ''}
        ${discountPercentage ? `<span class="tp-byob-card__badge tp-byob-card__badge--sale f-badge f-badge--sale">Save <span data-sale-value>${discountPercentage}%</span></span>` : ''}
      </div>
    `;
  }

  renderCardSwatches(product, selectedVariant) {
    const pcsVariants = this.getVariantsForPcs(product, selectedVariant);
    const variants = this.getUniqueShadeVariants(product, pcsVariants, selectedVariant);
    const firstLabel = this.normalizeVariantValue(this.getVariantLabel(product, variants[0]));
    const hasVisibleShades = variants.length > 1 || (variants.length === 1 && firstLabel && firstLabel !== 'default title');

    if (!hasVisibleShades) {
      return '<div class="tp-byob-card__swatches tp-byob-card__swatches--empty" aria-hidden="true"></div>';
    }

    const visibleVariants = variants.slice(0, 5);
    const hiddenCount = Math.max(variants.length - visibleVariants.length, 0);

    return `
      <div class="tp-byob-card__swatches" aria-label="${this.escape(product.title)} shades">
        ${visibleVariants.map((variant) => this.renderSwatch(product, variant, selectedVariant)).join('')}
        ${hiddenCount ? `<span class="tp-byob-card__swatch-more">+${hiddenCount}</span>` : ''}
      </div>
    `;
  }

  renderSwatch(product, variant, selectedVariant, attribute = 'data-swatch-variant') {
    const label = this.getVariantLabel(product, variant);
    const swatch = this.swatches[String(label).toLowerCase()];
    const style = swatch
      ? `--swatch:${this.escape(swatch)}`
      : variant.image
        ? `--swatch:#fff;background-image:url(${this.escape(variant.image)});background-size:cover;background-position:center;`
        : '--swatch:#fff';
    const badge = attribute === 'data-modal-swatch-variant'
      ? this.renderSwatchBadge(product, variant)
      : '';
    return `
      <button
        class="tp-byob-card__swatch ${selectedVariant?.id === variant.id ? 'is-active' : ''}"
        type="button"
        ${attribute}="${variant.id}"
        style="${style}"
        ${variant.available ? '' : 'disabled'}
        aria-label="${this.escape(label)}"
      >${badge}</button>
    `;
  }

  parseSkuList(raw) {
    return String(raw || '')
      .replace(/,/g, '\n')
      .split(/\n/)
      .map((line) => line.replace(/<[^>]*>/g, '').trim())
      .filter(Boolean);
  }

  getSwatchBadge(product, variant) {
    const label = String(this.getVariantLabel(product, variant) || '').trim();
    const hotColors = Array.isArray(product?.hotColors) ? product.hotColors : [];
    if (hotColors.some((color) => String(color).trim() === label)) {
      return { type: 'hot' };
    }

    const sku = String(variant?.sku || '').trim();
    if (!sku) return null;

    for (const tag of this.skuTags || []) {
      const skus = this.parseSkuList(tag?.skus);
      if (!skus.includes(sku)) continue;
      const position = ['left', 'center', 'right'].includes(String(tag.position || '').toLowerCase())
        ? String(tag.position).toLowerCase()
        : 'right';
      const width = Number(tag.width) > 0 ? Number(tag.width) : 20;
      if (tag?.icon) {
        return {
          type: 'sku',
          icon: tag.icon,
          position,
          width,
        };
      }
      const tagLabel = String(tag?.label || '').trim();
      if (!tagLabel) continue;
      return {
        type: 'sku-label',
        label: tagLabel,
        background: tag?.background || '',
        color: tag?.color || '',
        position,
      };
    }
    return null;
  }

  renderSwatchBadge(product, variant) {
    const badge = this.getSwatchBadge(product, variant);
    if (!badge) return '';
    if (badge.type === 'hot') {
      return `<span class="hot-icon-wrapper hot-icon-wrapper--hot" aria-hidden="true"><img class="hot-icon" src="https://cdn.shopify.com/s/files/1/0656/7745/9529/files/fire-icon.png?v=1773824812" alt="" loading="lazy" width="16" height="16"></span>`;
    }
    if (badge.type === 'sku-label') {
      return `<span class="hot-icon-wrapper hot-icon-wrapper--label hot-icon-wrapper--${this.escape(badge.position)}" style="--sku-tag-bg: ${this.escape(badge.background)}; --sku-tag-color: ${this.escape(badge.color)};" aria-hidden="true"><span class="hot-icon-label">${this.escape(badge.label)}</span></span>`;
    }
    return `<span class="hot-icon-wrapper hot-icon-wrapper--${this.escape(badge.position)}" style="--sku-tag-width: ${badge.width}px;" aria-hidden="true"><img class="hot-icon" src="${this.escape(badge.icon)}" alt="" loading="lazy" width="${badge.width}"></span>`;
  }

  getProductUrl(product, variant) {
    if (!product?.url) return '#';
    return `${product.url}${variant?.id ? `${product.url.includes('?') ? '&' : '?'}variant=${variant.id}` : ''}`;
  }

  getVariantImages(product, variant) {
    const images = Array.isArray(variant?.images) ? variant.images.filter(Boolean) : [];
    if (images.length) return images;
    if (variant?.image) return [variant.image];
    if (product?.image) return [product.image];
    return [];
  }

  openProductModal(card) {
    const product = this.findProduct(Number(card?.dataset?.productId));
    if (!product) return;

    const availableVariants = (product.variants || []).filter((variant) => variant.available);
    const selectedVariant = this.cardState.get(String(product.id))
      || this.getPreferredVariant(product)
      || availableVariants[0]
      || product.variants?.[0];
    if (!selectedVariant) return;

    this.modalProduct = product;
    this.modalVariant = selectedVariant;
    this.modalVariantGroup = 'all';
    this.modalImageIndex = 0;
    this.cardState.set(String(product.id), selectedVariant);
    if (!this.modalOnly) {
      this.trackByobEvent('byob_product_open', {
        product_id: String(product.id),
        product_title: product.title,
        variant_id: String(selectedVariant.id),
        variant_title: selectedVariant.title,
        category: this.activeTab,
        items: [this.productVariantToAnalyticsItem(product, selectedVariant)],
      });
    }
    this.renderProductModal();
    this.modalEl.hidden = false;
    document.documentElement.classList.add('tp-byob-modal-open');
    document.addEventListener('keydown', this.handleModalKeydown);
    this.modalEl.querySelector('[data-modal-close]')?.focus();
  }

  openProductModalById(productId, variantId) {
    const product = this.findProduct(Number(productId));
    const selectedVariant = (product?.variants || []).find((variant) => Number(variant.id) === Number(variantId));
    if (!product) return;
    if (selectedVariant) this.cardState.set(String(product.id), selectedVariant);
    this.openProductModal({ dataset: { productId: String(product.id) } });
  }

  closeProductModal() {
    if (!this.modalEl || this.modalEl.hidden) return;
    this.destroyModalLightbox();
    this.modalEl.hidden = true;
    this.modalEl.innerHTML = '';
    this.modalProduct = null;
    this.modalVariant = null;
    this.modalImageIndex = 0;
    document.documentElement.classList.remove('tp-byob-modal-open');
    document.removeEventListener('keydown', this.handleModalKeydown);
  }

  buildPreviewSrc(src) {
    if (!src) return '';
    try {
      const url = new URL(src, window.location.origin);
      url.searchParams.set('width', '1946');
      return url.toString();
    } catch (error) {
      return String(src);
    }
  }

  ensurePhotoSwipeReady() {
    if (window.FoxTheme?.PhotoSwipeLightbox && window.FoxTheme?.PhotoSwipe) {
      return Promise.resolve();
    }
    return new Promise((resolve, reject) => {
      let tries = 0;
      const timer = window.setInterval(() => {
        tries += 1;
        if (window.FoxTheme?.PhotoSwipeLightbox && window.FoxTheme?.PhotoSwipe) {
          window.clearInterval(timer);
          resolve();
          return;
        }
        if (tries >= 40) {
          window.clearInterval(timer);
          reject(new Error('PhotoSwipe unavailable'));
        }
      }, 50);
    });
  }

  destroyModalLightbox() {
    if (!this.modalLightbox) return;
    try {
      if (this.modalLightbox.pswp) this.modalLightbox.pswp.close();
      this.modalLightbox.destroy();
    } catch (error) {
      // PhotoSwipe may already be closed.
    }
    this.modalLightbox = null;
  }

  syncModalGalleryUi() {
    if (!this.modalEl) return;
    this.modalEl.querySelectorAll('[data-modal-slide]').forEach((img) => {
      img.classList.toggle('is-active', Number(img.dataset.modalSlide) === this.modalImageIndex);
    });
    this.modalEl.querySelectorAll('[data-modal-image-index]').forEach((dot) => {
      dot.classList.toggle('is-active', Number(dot.dataset.modalImageIndex) === this.modalImageIndex);
    });
    this.syncModalImageTrack();
  }

  async openModalImagePreview(startIndex) {
    if (!this.modalProduct || !this.modalVariant || this.modalEl?.hidden) return;
    const images = this.getVariantImages(this.modalProduct, this.modalVariant);
    if (!images.length) return;

    try {
      await this.ensurePhotoSwipeReady();
    } catch (error) {
      return;
    }

    const index = Math.min(
      Math.max(Number.isFinite(startIndex) ? startIndex : this.modalImageIndex, 0),
      images.length - 1,
    );
    this.modalImageIndex = index;
    this.syncModalGalleryUi();
    this.destroyModalLightbox();

    const dataSource = images.map((src, slideIndex) => {
      const img = this.modalEl.querySelector(`[data-modal-slide="${slideIndex}"]`);
      const width = img?.naturalWidth > 0 ? img.naturalWidth : 1200;
      const height = img?.naturalHeight > 0 ? img.naturalHeight : 1200;
      return {
        src: this.buildPreviewSrc(src),
        width,
        height,
      };
    });

    this.modalLightbox = new window.FoxTheme.PhotoSwipeLightbox({
      dataSource,
      pswpModule: window.FoxTheme.PhotoSwipe,
      bgOpacity: 1,
      arrowPrev: false,
      arrowNext: false,
      zoom: false,
      close: false,
      counter: false,
      preloader: false,
    });

    this.modalLightbox.addFilter('thumbEl', (thumbEl) => {
      const active = this.modalEl.querySelector('.tp-byob-modal__image.is-active, [data-modal-slide]');
      return active || thumbEl;
    });

    this.modalLightbox.addFilter('placeholderSrc', (placeholderSrc) => {
      const active = this.modalEl.querySelector('.tp-byob-modal__image.is-active');
      return active?.currentSrc || active?.src || placeholderSrc;
    });

    this.modalLightbox.on('change', () => {
      const currIndex = this.modalLightbox?.pswp?.currIndex;
      if (!Number.isFinite(currIndex) || currIndex === this.modalImageIndex) return;
      this.modalImageIndex = currIndex;
      this.syncModalGalleryUi();
    });

    this.modalLightbox.on('uiRegister', () => {
      if (images.length > 1) {
        this.modalLightbox.pswp.ui.registerElement({
          name: 'next',
          ariaLabel: this.strings.nextImage || 'Next image',
          order: 3,
          isButton: true,
          html: '<svg class="pswp-icon-next flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
          onClick: () => this.modalLightbox.pswp.next(),
        });
        this.modalLightbox.pswp.ui.registerElement({
          name: 'prev',
          ariaLabel: this.strings.previousImage || 'Previous image',
          order: 1,
          isButton: true,
          html: '<svg class="pswp-icon-prev rtl-flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
          onClick: () => this.modalLightbox.pswp.prev(),
        });
      }
      this.modalLightbox.pswp.ui.registerElement({
        name: 'close-zoom',
        ariaLabel: this.strings.close || 'Close',
        order: 2,
        isButton: true,
        html: '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>',
        onClick: () => this.modalLightbox.pswp.close(),
      });
    });

    this.modalLightbox.on('destroy', () => {
      this.modalLightbox = null;
    });

    this.modalLightbox.init();
    this.modalLightbox.loadAndOpen(index);
  }

  renderProductModal() {
    const product = this.modalProduct;
    let selectedVariant = this.modalVariant;
    if (!product || !selectedVariant) return;

    const visibleVariants = this.getModalVisibleVariants(product);
    if (visibleVariants.length && !visibleVariants.some((variant) => variant.id === selectedVariant.id)) {
      const pcsVariants = this.getVariantsForPcs(product, selectedVariant);
      const groups = this.getProductVariantGroups(product, pcsVariants);
      const activeGroup = groups.find((group) => group.id === this.modalVariantGroup) || null;
      selectedVariant = this.getPreferredVariant(product, visibleVariants, activeGroup)
        || visibleVariants.find((variant) => variant.available)
        || visibleVariants[0];
      this.modalVariant = selectedVariant;
      this.cardState.set(String(product.id), selectedVariant);
    }

    const images = this.getVariantImages(product, selectedVariant);
    const imageIndex = Math.min(Math.max(this.modalImageIndex, 0), Math.max(images.length - 1, 0));
    this.modalImageIndex = imageIndex;
    const image = images[imageIndex] || '';
    const label = this.getVariantLabel(product, selectedVariant);
    const shadeOption = this.getShadeOption(product);
    const swatchVariants = this.getUniqueShadeVariants(
      product,
      visibleVariants.length ? visibleVariants : this.getVariantsForPcs(product, selectedVariant),
      selectedVariant,
    );
    const isAvailable = Boolean(selectedVariant.available);
    const description = String(product.description || '').trim();
    const preorderNoticeHtml = this.modalOnly ? '' : String(product.preorderNoticeHtml || '').trim();
    const productUrl = this.getProductUrl(product, selectedVariant);
    const actionLabel = this.modalOnly
      ? (this.dataset.modalActionLabel || 'Select shade')
      : (this.strings.add || this.strings.addFallback);

    this.modalEl.innerHTML = `
      <div class="tp-byob-modal__dialog${preorderNoticeHtml ? ' tp-byob-modal__dialog--preorder' : ''}">
        <button class="tp-byob-modal__close" type="button" data-modal-close aria-label="${this.escape(this.strings.close || 'Close')}">&times;</button>
        <div class="tp-byob-modal__media">
          <div class="tp-byob-modal__image-wrap" data-modal-image-track>
            ${images.map((item, index) => `
              <img
                class="tp-byob-modal__image ${index === imageIndex ? 'is-active' : ''}"
                src="${this.escape(item)}"
                alt="${this.escape(product.title)}"
                width="700"
                height="700"
                data-modal-slide="${index}"
                title="${this.escape(this.strings.zoomImage || 'Zoom image')}"
              >
            `).join('')}
            ${images.length > 1 ? `
              <button class="tp-byob-modal__nav tp-byob-modal__nav--prev" type="button" data-modal-gallery="-1" aria-label="${this.escape(this.strings.previousImage || 'Previous image')}"></button>
              <button class="tp-byob-modal__nav tp-byob-modal__nav--next" type="button" data-modal-gallery="1" aria-label="${this.escape(this.strings.nextImage || 'Next image')}"></button>
            ` : ''}
          </div>
          ${images.length > 1 ? `
            <div class="tp-byob-modal__dots" aria-label="${this.escape(this.strings.productImages || 'Product images')}">
              ${images.map((_, index) => `
                <button
                  class="tp-byob-modal__dot ${index === imageIndex ? 'is-active' : ''}"
                  type="button"
                  data-modal-image-index="${index}"
                  aria-label="${this.escape(this.strings.showImage || 'Show image')} ${index + 1}"
                ></button>
              `).join('')}
            </div>
          ` : ''}
        </div>
        <div class="tp-byob-modal__info">
          <h3 class="tp-byob-modal__title">${this.escape(product.title)}</h3>
          ${description ? `<p class="tp-byob-modal__description">${this.escape(description)}</p>` : ''}
          <p class="tp-byob-modal__price">${this.formatGoalMoney(selectedVariant.price || 0)}</p>
          ${this.renderModalPcsOptions(product, selectedVariant)}
          ${label ? `<p class="tp-byob-modal__variant">${this.escape(shadeOption?.name || this.strings.color || 'Color')}: ${this.escape(label)}</p>` : ''}
          ${this.renderModalVariantGroups(product)}
          <div class="tp-byob-modal__swatches" aria-label="${this.escape(product.title)} ${this.escape(this.strings.shades || 'shades')}">
            ${swatchVariants.map((variant) => this.renderSwatch(product, variant, selectedVariant, 'data-modal-swatch-variant')).join('')}
          </div>
          ${preorderNoticeHtml ? `<div class="tp-byob-modal__preorder">${preorderNoticeHtml}</div>` : ''}
          <button class="tp-byob-modal__add" type="button" data-modal-add-product ${isAvailable ? '' : 'disabled'}>
            ${this.escape(isAvailable ? actionLabel : (this.strings.soldOut || 'Sold out'))}
          </button>
          <p class="tp-byob-modal__message" data-modal-message hidden></p>
          <a class="tp-byob-modal__details" href="${this.escape(productUrl)}">${this.escape(this.strings.viewProductDetails || 'View product details')}</a>
        </div>
      </div>
    `;
    this.syncModalImageTrack();
  }

  moveModalImage(direction) {
    if (!this.modalProduct || !this.modalVariant || this.modalEl.hidden) return;
    const images = this.getVariantImages(this.modalProduct, this.modalVariant);
    if (images.length < 2) return;
    this.modalImageIndex = (this.modalImageIndex + direction + images.length) % images.length;
    this.renderProductModal();
  }

  selectModalImage(index) {
    if (!this.modalProduct || !this.modalVariant || this.modalEl.hidden) return;
    const images = this.getVariantImages(this.modalProduct, this.modalVariant);
    if (!images.length || index < 0 || index >= images.length) return;
    this.modalImageIndex = index;
    this.renderProductModal();
  }

  selectModalVariantGroup(groupId) {
    if (!this.modalProduct || this.modalEl.hidden) return;
    this.modalVariantGroup = groupId || 'all';
    const pcsVariants = this.getVariantsForPcs(this.modalProduct, this.modalVariant);
    const groups = this.getProductVariantGroups(this.modalProduct, pcsVariants);
    const activeGroup = groups.find((group) => group.id === this.modalVariantGroup) || groups[0] || null;
    const visibleVariants = this.getModalVisibleVariants(this.modalProduct);

    if (visibleVariants.length) {
      this.modalVariant = this.getPreferredVariant(this.modalProduct, visibleVariants, activeGroup)
        || visibleVariants.find((variant) => variant.available)
        || visibleVariants[0];
      this.cardState.set(String(this.modalProduct.id), this.modalVariant);
      const card = this.productsEl?.querySelector(`[data-product-id="${this.modalProduct.id}"]`);
      if (card) card.outerHTML = this.renderProductCard(this.modalProduct);
    }
    this.modalImageIndex = 0;
    this.renderProductModal();
  }

  selectModalPcs(pcsValue) {
    const product = this.modalProduct;
    if (!product || this.modalEl.hidden) return;

    const variant = this.getTargetVariantForPcs(product, pcsValue, this.modalVariant);
    if (!variant) return;

    this.modalVariant = variant;
    this.modalVariantGroup = 'all';
    this.modalImageIndex = 0;
    this.cardState.set(String(product.id), variant);
    const card = this.productsEl?.querySelector(`[data-product-id="${product.id}"]`);
    if (card) card.outerHTML = this.renderProductCard(product);
    this.renderProductModal();
  }

  syncModalImageTrack() {
    const track = this.modalEl.querySelector('[data-modal-image-track]');
    if (!track) return;
    window.requestAnimationFrame(() => {
      track.scrollLeft = track.clientWidth * this.modalImageIndex;
    });
  }

  updateModalImageFromScroll(track) {
    if (!track || !track.clientWidth) return;
    const index = Math.round(track.scrollLeft / track.clientWidth);
    if (index === this.modalImageIndex) return;
    this.modalImageIndex = index;
    this.modalEl.querySelectorAll('[data-modal-image-index]').forEach((dot) => {
      dot.classList.toggle('is-active', Number(dot.dataset.modalImageIndex) === index);
    });
  }

  selectModalVariant(variantId) {
    const product = this.modalProduct;
    const variant = product?.variants?.find((item) => item.id === variantId);
    if (!product || !variant || !variant.available) return;

    this.modalVariant = variant;
    this.modalImageIndex = 0;
    this.cardState.set(String(product.id), variant);
    const card = this.productsEl?.querySelector(`[data-product-id="${product.id}"]`);
    if (card) card.outerHTML = this.renderProductCard(product);
    this.renderProductModal();
  }

  addModalProduct(button) {
    if (!button || button.disabled) return;
    if (this.modalOnly) {
      if (!this.modalProduct || !this.modalVariant || !this.modalVariant.available) return;
      this.dispatchEvent(new CustomEvent('timephoria:byob-modal-select', {
        bubbles: true,
        detail: {
          productId: this.modalProduct.id,
          variantId: this.modalVariant.id,
        },
      }));
      button.disabled = true;
      button.classList.add('is-added');
      button.textContent = this.dataset.modalAddedLabel || this.strings.added || 'Selected';
      window.setTimeout(() => this.closeProductModal(), 350);
      return;
    }
    const maxItems = this.maxItems;
    const messageEl = this.modalEl.querySelector('[data-modal-message]');
    if (maxItems && this.selected.length >= maxItems) {
      const message = this.dataset.maxMessage || '';
      this.limitMessageEl.textContent = message;
      this.limitMessageEl.hidden = false;
      if (messageEl) {
        messageEl.textContent = message;
        messageEl.hidden = false;
      }
      return;
    }

    if (!this.modalProduct || !this.modalVariant || !this.modalVariant.available) return;
    this.addVariantToSelection(this.modalProduct, this.modalVariant, 'modal');
    button.disabled = true;
    button.classList.add('is-added');
    button.textContent = this.strings.added || 'Added';
    if (messageEl) messageEl.hidden = true;
    window.setTimeout(() => {
      if (!this.modalEl.hidden) {
        button.classList.remove('is-added');
        button.classList.add('is-restoring');
        button.textContent = this.strings.add || this.strings.addFallback;
        button.disabled = false;
        window.setTimeout(() => {
          button.classList.remove('is-restoring');
        }, 260);
      }
    }, 850);
    this.renderSummary();
  }

  selectCardVariant(card, variantId) {
    if (!card) return;
    const product = this.findProduct(Number(card.dataset.productId));
    const variant = product?.variants?.find((item) => item.id === variantId);
    if (!product || !variant || !variant.available) return;
    this.cardState.set(String(product.id), variant);
    card.outerHTML = this.renderProductCard(product);
  }

  addProduct(card, button) {
    if (!card || !button || button.disabled) return;
    const maxItems = this.maxItems;
    if (maxItems && this.selected.length >= maxItems) {
      this.limitMessageEl.textContent = this.dataset.maxMessage || '';
      this.limitMessageEl.hidden = false;
      return;
    }

    const product = this.findProduct(Number(card.dataset.productId));
    const variant = this.cardState.get(String(product?.id));
    if (!product || !variant || !variant.available) return;

    this.addVariantToSelection(product, variant, 'card');
    button.disabled = true;
    button.classList.add('is-added');
    button.textContent = this.strings.added;
    window.setTimeout(() => {
      button.classList.remove('is-added');
      button.classList.add('is-restoring');
      button.textContent = this.strings.add || this.strings.addFallback;
      button.disabled = false;
      window.setTimeout(() => {
        button.classList.remove('is-restoring');
      }, 260);
    }, 850);
    this.renderSummary();
  }

  addVariantToSelection(product, variant, addMethod = 'card') {
    if (!product || !variant || !variant.available) return;
    const previousCount = this.selected.length;
    const selectedItem = this.buildSelectedItem(product, variant);
    this.selected.unshift(selectedItem);
    const itemCount = this.selected.length;

    this.trackByobEvent('byob_add_item', {
      add_method: addMethod,
      product_id: String(product.id),
      product_title: product.title,
      variant_id: String(variant.id),
      variant_title: variant.title,
      category: this.activeTab,
      item_count: itemCount,
      bundle_value: this.centsToAnalyticsValue(this.selectedSubtotal),
      items: [this.productVariantToAnalyticsItem(product, variant)],
    });

    this.trackByobEvent('byb_item_add', {
      ...this.bybBaseAnalyticsParams,
      byb_item_product_id: String(product.id),
      byb_item_handle: product.handle || '',
      byb_item_title: product.title,
      byb_item_variant_id: String(variant.id),
      byb_item_variant_title: variant.title,
      byb_item_sku: variant.sku || '',
      byb_item_price: this.centsToAnalyticsValue(variant.price),
      value: this.centsToAnalyticsValue(variant.price),
      items: [this.productVariantToAnalyticsItem(product, variant)],
    });

    if (!this.bundleConfiguredTracked && previousCount < this.minItems && itemCount >= this.minItems) {
      this.bundleConfiguredTracked = true;
      this.trackByobEvent('byob_bundle_configured', {
        item_count: itemCount,
        bundle_value: this.centsToAnalyticsValue(this.selectedSubtotal),
        items: this.selectedItemsToAnalyticsItems(),
      });
    }

    this.showMobileAddFeedback(selectedItem);
  }

  findProduct(productId) {
    for (const tab of this.tabs) {
      const found = (tab.products || []).find((product) => product.id === productId);
      if (found) return found;
    }
    return null;
  }

  findProductByVariantId(variantId) {
    for (const tab of this.tabs) {
      for (const product of (tab.products || [])) {
        const variant = (product.variants || []).find((item) => Number(item.id) === Number(variantId));
        if (variant) return { tab, product, variant };
      }
    }
    return null;
  }

  renderProgress() {
    const tiers = this.tiers;

    if (!tiers.length) {
      this.querySelectorAll('.tp-byob__progress').forEach((el) => {
        el.innerHTML = '';
        el.hidden = true;
      });
      return;
    }

    const progress = this.getTierProgress(tiers);
    const railEnd = this.getProgressRailEnd(tiers);
    const railProgress = railEnd > 0 ? Math.min(100, (progress / railEnd) * 100) : progress;
    const next = this.nextTier;
    const current = this.currentTier;
    let message = '';

    if (next) {
      message = this.fillTierText(this.tierMessageTemplate(next, false), next);
    } else if (current) {
      message = this.fillTierText(current.reachedText || '', current);
    }

    const markup = `
      <p class="tp-byob-progress__message">${this.escape(message)}</p>
      <div class="tp-byob-progress__rail" style="--progress:${railProgress}%;--rail-end:${railEnd}%">
        <span class="tp-byob-progress__fill"></span>
      </div>
      <div class="tp-byob-progress__steps" style="--tier-count:${Math.max(tiers.length, 1)}">
        ${tiers.map((tier) => `
          <div class="tp-byob-progress__step ${this.tierReached(tier) ? 'is-reached' : ''}">
            <span class="tp-byob-progress__dot">${this.tierReached(tier) ? '✓' : ''}</span>
            <span class="tp-byob-progress__threshold">${this.escape(this.resolvedLabel(tier))}</span>
          </div>
        `).join('')}
      </div>
    `;

    this.querySelectorAll('.tp-byob__progress').forEach((el) => {
      el.hidden = false;
      el.innerHTML = markup;
    });
  }

  tierMessageTemplate(tier, reached) {
    const template = reached ? tier.reachedText : tier.beforeText;
    if (this.isItemThreshold(tier)) {
      return template || 'Add {count} more {item_label} to unlock {discount}';
    }
    const usesItemCount = /\{count\}|\{amount\}/.test(template || '');
    const usesMoney = /\{money\}|\{remaining\}/.test(template || '');
    if (!template || (usesItemCount && !usesMoney)) return 'Spend {money} more to unlock {discount}';
    return template;
  }

  tierDiscountLabel(tier) {
    if (!tier) return '';
    if (this.isAmountReward(tier)) {
      return this.formatProgressMoney(this.convertBaseCentsToCurrentCents(tier.discountAmountUsdCents));
    }
    return `${Number(tier.discount || 0)}%`;
  }

  resolvedLabel(tier) {
    const template = String(tier?.label || 'buy {target} Save {discount}').replace(/\{label\}/g, '');
    const text = this.applyTierTokens(template, tier, '');
    if (this.tiers.length < 3) return text;
    return text.replace(/\s+Save\s+/i, '\nSave ');
  }

  fillTierText(template, tier) {
    return this.applyTierTokens(template, tier, this.resolvedLabel(tier));
  }

  applyTierTokens(template, tier, label) {
    const target = this.tierTarget(tier);
    const remaining = Math.max(0, target - this.tierProgressValue(tier));
    const isItem = this.isItemThreshold(tier);
    const neededCount = isItem ? remaining : 0;
    const money = isItem ? '' : this.formatProgressMoney(remaining);
    const goal = isItem ? String(tier.count) : this.formatProgressMoney(target);
    const remainingLabel = isItem ? String(neededCount) : money;
    return String(template || '')
      .replace(/\{amount\}/g, String(neededCount))
      .replace(/\{count\}/g, String(neededCount))
      .replace(/\{item_label\}/g, neededCount === 1 ? (this.strings.item || 'item') : (this.strings.items || 'items'))
      .replace(/\{remaining\}/g, remainingLabel)
      .replace(/\{target\}/g, goal)
      .replace(/\{money\}/g, money)
      .replace(/\{goal\}/g, goal)
      .replace(/\{label\}/g, label || '')
      .replace(/\{discount\}/g, this.tierDiscountLabel(tier));
  }

  getTierProgress(tiers) {
    if (!tiers.length) return 0;
    if (tiers.every((tier) => this.isItemThreshold(tier))) {
      return this.getQuantityTierProgress(this.selected.length, tiers);
    }

    const positions = tiers.map((_, index) => ((index + 0.5) / tiers.length) * 100);
    let filled = 0;

    for (let index = 0; index < tiers.length; index += 1) {
      const target = this.tierTarget(tiers[index]) || 1;
      const ratio = Math.min(1, Math.max(0, this.tierProgressValue(tiers[index]) / target));
      const start = index === 0 ? 0 : positions[index - 1];
      if (ratio < 1) return start + ratio * (positions[index] - start);
      filled = positions[index];
    }

    return filled;
  }

  getQuantityTierProgress(count, tiers) {
    if (tiers.length === 1) {
      const threshold = tiers[0].count || 1;
      return count >= threshold ? 100 : Math.min(50, (count / threshold) * 50);
    }

    const positions = tiers.map((_, index) => ((index + 0.5) / tiers.length) * 100);

    if (count <= tiers[0].count) {
      return Math.max(0, (count / tiers[0].count) * positions[0]);
    }

    for (let index = 1; index < tiers.length; index += 1) {
      const previous = tiers[index - 1];
      const current = tiers[index];
      if (count <= current.count) {
        const range = current.count - previous.count || 1;
        const ratio = (count - previous.count) / range;
        return positions[index - 1] + ratio * (positions[index] - positions[index - 1]);
      }
    }

    return positions[positions.length - 1] || 100;
  }

  getProgressRailEnd(tiers) {
    if (!tiers.length) return 100;
    if (tiers.length === 1) return 100;

    return ((tiers.length - 0.5) / tiers.length) * 100;
  }

  renderSummary() {
    this.renderProgress();
    this.saveSelectedToStorage();
    const total = this.selectedSubtotal;
    const pricing = this.bundlePricing;
    const discount = pricing.amount;
    const finalTotal = total - discount;
    const slotCount = this.getSummarySlotCount();
    const shouldScroll = slotCount > 3;

    if (this.summaryCountEl) {
      this.summaryCountEl.textContent = this.formatSelectedCountLabel(this.selected.length);
    }

    this.selectedWrapEl.classList.add('has-items');
    this.selectedWrapEl.classList.toggle('is-scrollable', shouldScroll);
    this.selectedEl.classList.toggle('is-scrollable', shouldScroll);

    const selectedMarkup = this.selected.map((item) => `
      <article class="tp-byob-selected">
        <div class="tp-byob-selected__image">
          ${item.image ? `<img src="${this.escape(item.image)}" alt="${this.escape(item.productTitle)}" loading="lazy" width="160" height="160">` : ''}
        </div>
        <button class="tp-byob-selected__remove" type="button" data-remove-selected="${this.escape(item.uid)}" aria-label="${this.escape(this.strings.remove)} ${this.escape(item.productTitle)}">×</button>
        <span class="tp-byob-selected__title">${this.escape(item.productTitle)}</span>
        <span class="tp-byob-selected__variant">${this.escape(item.variantTitle)}</span>
      </article>
    `).join('');

    const emptyCount = Math.max(0, slotCount - this.selected.length);
    const emptyMarkup = Array.from({ length: emptyCount }, (_, index) => {
      const offerLabel = this.getSlotOfferLabel(this.selected.length + index + 1);
      if (!offerLabel) return '<div class="tp-byob-slot" aria-hidden="true">+</div>';
      return `<div class="tp-byob-slot tp-byob-slot--offer" aria-hidden="true">${this.escape(offerLabel)}</div>`;
    }).join('');
    this.selectedEl.innerHTML = `${selectedMarkup}${emptyMarkup}`;
    this.emptySlotsEl.innerHTML = '';

    if (!this.maxItems || this.selected.length < this.maxItems) {
      this.limitMessageEl.hidden = true;
    }

    this.subtotalEl.innerHTML = discount > 0
      ? `<span class="tp-byob__subtotal-price"><span class="tp-byob__subtotal-compare">${this.formatGoalMoney(total)}</span><span class="tp-byob__subtotal-sale">${this.formatGoalMoney(finalTotal)}</span><span class="tp-byob__subtotal-badge">${this.escape(pricing.badge)}</span></span>`
      : `<span class="tp-byob__subtotal-price">${this.formatGoalMoney(total)}</span>`;

    const hasBundleItems = this.selected.length > 0;
    this.viewDetailButton?.classList.toggle('is-available', hasBundleItems);
    if (!hasBundleItems) this.setVariantDetailOpen(false);
    this.addBundleButton.disabled = this.adding || !hasBundleItems;
    this.addBundleButton.textContent = this.adding ? this.strings.addingBundle : this.strings.addBundle;
    this.updateScrollIndicator();
  }

  updateScrollIndicator() {
    if (!this.scrollIndicator || !this.selectedEl) return;
    const isScrollable = this.selectedWrapEl?.classList.contains('is-scrollable');
    const max = this.selectedEl.scrollWidth - this.selectedEl.clientWidth;
    const percent = max > 0 ? this.selectedEl.scrollLeft / max : 0;
    const track = this.selectedEl.clientWidth * 0.66;
    this.scrollIndicator.style.setProperty('--scroll-x', `${Math.max(0, track * percent)}px`);
    if (this.selectedPrevButton) this.selectedPrevButton.hidden = !isScrollable || this.selectedEl.scrollLeft <= 2;
    if (this.selectedNextButton) this.selectedNextButton.hidden = !isScrollable || this.selectedEl.scrollLeft >= max - 2;
  }

  scrollSelectedItems(direction) {
    if (!this.selectedEl) return;
    const distance = this.selectedEl.clientWidth * 0.72;
    this.selectedEl.scrollBy({ left: distance * direction, behavior: 'smooth' });
  }

  async addBundleToCart() {
    if (this.adding || this.selected.length < this.minItems) return;
    this.adding = true;
    this.renderSummary();

    const bundleId = `tp-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const pricing = this.bundlePricing;
    const bundleDiscountPercent = pricing.percent;
    const grouped = this.selected.reduce((map, item) => {
      const key = String(item.variantId);
      map[key] = map[key] || { item, quantity: 0 };
      map[key].quantity += 1;
      return map;
    }, {});

    const items = Object.values(grouped).map(({ item, quantity }) => {
      const cartItem = {
        id: item.variantId,
        quantity,
      };

      if (this.groupCartItems) {
        cartItem.properties = {
          _tp_bundle_id: bundleId,
          _tp_bundle_title: this.dataset.bundleTitle || 'Build your bundle',
          _tp_bundle_image: this.dataset.bundleImage || '',
          _tp_bundle_discount_percent: String(bundleDiscountPercent),
          _tp_bundle_discount_amount: String(pricing.amount),
          _tp_bundle_item_title: item.productTitle,
          _tp_bundle_item_variant: item.variantTitle,
          _byob_bundle: 'true',
          _byob_source: this.byobAttribution.byob_source || 'direct',
          _byob_session_id: this.byobAttribution.byob_session_id || '',
        };
      }

      return cartItem;
    });

    try {
      const sectionsToBundle = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
      );
      const drawer = document.querySelector('cart-drawer');

      const body = JSON.stringify({
        items,
        sections: sectionsToBundle,
        section_url: window.location.pathname,
        sections_url: window.location.pathname,
      });
      const response = await fetch(
        window.FoxTheme?.routes?.cart_add_url || '/cart/add.js',
        window.FoxTheme?.utils?.fetchConfig
          ? { ...window.FoxTheme.utils.fetchConfig('javascript'), body }
          : {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
              body,
            }
      );
      const added = await response.json();
      if (!response.ok || added.status) throw new Error(added.description || 'Failed to add bundle');

      const cartResponse = await fetch(window.FoxTheme?.routes?.cart_url || '/cart.js', {
        ...(window.FoxTheme?.utils?.fetchConfig ? window.FoxTheme.utils.fetchConfig() : {}),
      });
      const cart = await cartResponse.json();
      cart.sections = added.sections || {};

      if (window.FoxTheme?.pubsub && window.FoxTheme?.pubsub?.PUB_SUB_EVENTS?.cartUpdate) {
        window.FoxTheme.pubsub.publish(window.FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
          cart,
        });
      }

      this.trackByobEvent('byob_add_bundle_to_cart', {
        bundle_id: bundleId,
        item_count: this.selected.length,
        bundle_value: this.centsToAnalyticsValue(this.selectedSubtotal),
        discount_percent: bundleDiscountPercent,
        items: this.selectedItemsToAnalyticsItems(),
      });

      this.trackByobEvent('byb_add_to_cart', {
        ...this.bybBaseAnalyticsParams,
        byb_combo_id: this.buildBybComboId(),
        byb_item_count: this.selected.length,
        byb_contents: this.buildBybContents(),
        byb_sku_contents: this.buildBybSkuContents(),
        byb_value: this.centsToAnalyticsValue(this.selectedFinalTotal),
        value: this.centsToAnalyticsValue(this.selectedFinalTotal),
        items: this.selectedItemsToAnalyticsItems(),
      });

      this.selected = [];
      this.bundleConfiguredTracked = false;
      this.clearSelectedStorage();
      document.dispatchEvent(new CustomEvent('product-ajax:added', { detail: { product: added } }));
      if (drawer && typeof drawer.show === 'function') {
        drawer.show(this.addBundleButton);
      }

      this.hideMobileAddToast();
      this.setSummaryExpanded(false);
    } catch (error) {
      console.error(error);
    } finally {
      this.adding = false;
      this.renderSummary();
    }
  }

  escape(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
}

customElements.define('timephoria-bundle-builder', TimephoriaBundleBuilder);
})();
